if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this.dataset.productAvailable === 'false') return;

        this.sectionId = this.dataset.sectionId;
        this.productId = this.dataset.productId;
        this.productUrl = this.dataset.productUrl;

        this.bar = this.querySelector('.sticky-add-to-cart');
        this.image = this.querySelector('[data-sticky-image]');
        this.titleEl = this.querySelector('[data-sticky-title]');
        this.priceContainer = this.querySelector('[data-sticky-price]');
        this.variantToggle = this.querySelector('[data-variant-toggle]');
        this.variantPopover = this.querySelector('[data-variant-popover]');
        this.variantNameEl = this.querySelector('[data-variant-name]');
        this.submitButton = this.querySelector('[data-sticky-submit]');
        this.hiddenInput = this.querySelector('[data-sticky-variant-id]');
        this.hiddenForm = this.querySelector('[data-sticky-form]');

        this.currentVariantId = this.hiddenInput?.value || '';
        this.observedProductForm = null;

        // Parse variant data injected by Liquid
        this.loadVariantData();

        this.setupIntersectionObserver();
        this.setupVariantPopover();
        this.setupSubmitButton();

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );

        this.cartUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          this.handleCartUpdate.bind(this)
        );

        this.cartErrorUnsubscriber = subscribe(
          PUB_SUB_EVENTS.cartError,
          this.handleCartError.bind(this)
        );
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        this.intersectionObserver?.disconnect();
        document.removeEventListener('click', this.outsideClickHandler);
      }

      /* ================================================================== */

      setupIntersectionObserver() {
        const target = this.getObservableTarget();
        if (!target) {
          if (this.bar) {
            this.bar.classList.add('is-visible');
            document.body.classList.add('sticky-atc-visible');
          }
          return;
        }

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                this.bar.classList.add('is-visible');
                document.body.classList.add('sticky-atc-visible');
              } else {
                this.bar.classList.remove('is-visible');
                document.body.classList.remove('sticky-atc-visible');
              }
            });
          },
          { root: null, rootMargin: '0px', threshold: 0 }
        );

        this.intersectionObserver.observe(target);
        this.observedProductForm = target;
      }

      getObservableTarget() {
        const pi = document.querySelector('product-info');
        if (pi) {
          const form = pi.querySelector('product-form');
          if (form) return form;
        }
        const anyForm = document.querySelector('product-form');
        if (anyForm) return anyForm;
        const btns = document.querySelector('.product-form__buttons');
        if (btns) return btns;
        if (pi) return pi;
        const submit = document.querySelector('.product-form__submit');
        if (submit) return submit;
        return null;
      }

      /* ================================================================== */

      /**
       * Load variant data from the JSON script tag injected by Liquid.
       * This is the source of truth for independent popover selection.
       */
      loadVariantData() {
        const dataEl = this.querySelector('[data-variant-data]');
        if (!dataEl) return;
        try {
          const data = JSON.parse(dataEl.textContent);
          this.productOptions = data.options || [];
          this.variants = data.variants || [];
          // Build a lookup map: "option1|option2|..." → variant object
          this.variantMap = new Map();
          this.variants.forEach(v => {
            const key = v.options.join('|');
            this.variantMap.set(key, v);
          });
        } catch (e) {
          console.error('Sticky ATC: failed to parse variant data', e);
          this.productOptions = [];
          this.variants = [];
          this.variantMap = new Map();
        }
      }

      /**
       * Find a variant by the currently selected options in the popover.
       */
      findVariantBySelections() {
        if (!this.variantMap || this.productOptions.length === 0) return null;
        const selected = this.getSelectedPopoverOptions();
        const key = selected.join('|');
        return this.variantMap.get(key) || null;
      }

      /**
       * Get currently selected option values from the popover buttons.
       */
      getSelectedPopoverOptions() {
        const selected = [];
        this.productOptions.forEach((optName) => {
          const btn = this.variantPopover?.querySelector(
            `[data-option-name="${CSS.escape(optName)}"][aria-pressed="true"]`
          );
          selected.push(btn ? btn.dataset.optionValue : '');
        });
        return selected;
      }

      /**
       * Update disabled states of popover buttons based on current selection.
       * For each option button, check if there exists at least one available
       * variant that includes that option value combined with the currently
       * selected values at the OTHER option levels (wildcard if empty).
       *
       * Second pass: if a selected option becomes disabled, auto-select
       * the first available option at that level.
       */
      updatePopoverDisabledStates() {
        if (!this.variantMap || this.productOptions.length === 0) return;

        // Iterate twice to stabilize: first pass may disable a selected
        // option at one level, which affects constraints at other levels.
        for (let iteration = 0; iteration < 2; iteration++) {
          this.productOptions.forEach((optName, optIndex) => {
            const buttons = this.variantPopover?.querySelectorAll(
              `[data-option-name="${CSS.escape(optName)}"][data-option-value]`
            );
            if (!buttons) return;

            const constraints = [];
            this.productOptions.forEach((otherName, otherIdx) => {
              if (otherIdx === optIndex) return;
              const btn = this.variantPopover?.querySelector(
                `[data-option-name="${CSS.escape(otherName)}"][aria-pressed="true"]`
              );
              constraints.push({ index: otherIdx, value: btn ? btn.dataset.optionValue : '' });
            });

            buttons.forEach((btn) => {
              const hasMatch = this.variants.some((v) => {
                if (!v.available) return false;
                if (v.options[optIndex] !== btn.dataset.optionValue) return false;
                return constraints.every((c) =>
                  c.value === '' || v.options[c.index] === c.value
                );
              });
              if (hasMatch) {
                btn.removeAttribute('disabled');
              } else {
                btn.setAttribute('disabled', 'disabled');
              }
            });
          });

          // Auto-select first available if current selection is disabled
          this.productOptions.forEach((optName) => {
            const selectedBtn = this.variantPopover?.querySelector(
              `[data-option-name="${CSS.escape(optName)}"][aria-pressed="true"]`
            );
            if (selectedBtn && selectedBtn.hasAttribute('disabled')) {
              const firstAvailable = this.variantPopover?.querySelector(
                `[data-option-name="${CSS.escape(optName)}"][data-option-value]:not([disabled])`
              );
              if (firstAvailable) {
                selectedBtn.removeAttribute('aria-pressed');
                firstAvailable.setAttribute('aria-pressed', 'true');
              }
            }
          });
        }
      }

      /**
       * Update the sticky bar state from a variant object.
       * Called both from independent popover selection and from page sync.
       */
      applyVariant(variant) {
        if (!variant) return;

        this.currentVariantId = variant.id;
        if (this.hiddenInput) this.hiddenInput.value = variant.id;

        this.updatePrice(variant);
        this.updateImage(variant);
        this.updateVariantName(variant);
        this.updateSubmitButton(variant);
      }

      handleVariantChange({ data: { variant, html } }) {
        if (!variant) return;

        // Sync FROM page TO popover (one-way)
        // Update popover button states to match the page's current selection
        if (html) {
          this.syncPopoverFromPage(html);
        }

        // Apply variant to sticky bar (price, image, button)
        this.applyVariant(variant);

        const newTarget = this.getObservableTarget();
        if (newTarget && newTarget !== this.observedProductForm) {
          this.intersectionObserver?.disconnect();
          this.intersectionObserver.observe(newTarget);
          this.observedProductForm = newTarget;
        }
      }

      updatePrice(variant) {
        if (!this.priceContainer) return;

        const isOnSale = variant.compare_at_price &&
                         variant.compare_at_price > 0 &&
                         variant.compare_at_price > variant.price;

        const fmt = (cents) => {
          if (window.theme?.moneyFormat) {
            return window.theme.moneyFormat
              .replace('{{amount}}', (cents / 100).toFixed(2).replace('.', ','));
          }
          return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
        };

        this.priceContainer.innerHTML = '';

        if (isOnSale) {
          const c = document.createElement('span');
          c.className = 'sticky-add-to-cart__price-compare';
          c.textContent = fmt(variant.compare_at_price);
          this.priceContainer.appendChild(c);

          const s = document.createElement('span');
          s.className = 'sticky-add-to-cart__price-sale';
          s.textContent = fmt(variant.price);
          this.priceContainer.appendChild(s);
        } else {
          const r = document.createElement('span');
          r.className = 'sticky-add-to-cart__price-regular';
          r.textContent = fmt(variant.price);
          this.priceContainer.appendChild(r);
        }
      }

      updateImage(variant) {
        if (!this.image) return;
        if (variant.featured_image) {
          const src = typeof variant.featured_image === 'string'
            ? variant.featured_image
            : variant.featured_image.src;
          if (src) {
            this.image.src = src;
            this.image.removeAttribute('srcset');
            this.image.alt = variant.featured_image.alt || this.image.alt || '';
          }
        }
      }

      updateVariantName(variant) {
        if (variant.title && variant.title !== 'Default Title') {
          this.variantNameEl.textContent = variant.title;
        }
      }

      updateSubmitButton(variant) {
        if (!this.submitButton) return;
        if (!variant.available) {
          this.submitButton.setAttribute('disabled', 'disabled');
          const t = this.submitButton.querySelector('[data-submit-text]');
          if (t) t.textContent = window.variantStrings?.soldOut || 'Esaurito';
        } else {
          this.submitButton.removeAttribute('disabled');
          const t = this.submitButton.querySelector('[data-submit-text]');
          if (t) t.textContent = window.variantStrings?.addToCart || 'Aggiungi al carrello';
        }
      }

      /* ================================================================== */

      /**
       * Sync popover button states from the server response HTML.
       * Reads which options are selected/disabled on the main page variant-selects
       * and mirrors them into the popover. One-way: page → popover.
       */
      syncPopoverFromPage(html) {
        if (!this.variantPopover) return;

        const updatedSelects = html.querySelector('variant-selects');
        if (!updatedSelects) return;

        const state = new Map();

        if (updatedSelects.querySelector('fieldset')) {
          updatedSelects.querySelectorAll('input[type="radio"]').forEach((inp) => {
            const legend = inp.closest('fieldset')?.querySelector('legend');
            const name = legend?.textContent?.trim() || '';
            const isDisabled = inp.classList.contains('disabled') || inp.disabled;
            state.set(`${name}::${inp.value}`, { checked: inp.checked, disabled: isDisabled });
          });
        } else {
          updatedSelects.querySelectorAll('select').forEach((sel) => {
            const name = sel.name.replace('options[', '').replace(']', '');
            Array.from(sel.options).forEach((opt) => {
              if (!opt.value) return;
              state.set(`${name}::${opt.value}`, { checked: opt.selected, disabled: opt.disabled });
            });
          });
        }

        this.variantPopover.querySelectorAll('[data-option-value]').forEach((btn) => {
          const s = state.get(`${btn.dataset.optionName}::${btn.dataset.optionValue}`);
          if (!s) return;
          if (s.checked) btn.setAttribute('aria-pressed', 'true');
          else btn.removeAttribute('aria-pressed');
          if (s.disabled) btn.setAttribute('disabled', 'disabled');
          else btn.removeAttribute('disabled');
        });
      }

      /* ================================================================== */

      setupVariantPopover() {
        if (!this.variantToggle) return;
        if (this.dataset.hasMultipleVariants !== 'true') {
          this.variantToggle.style.display = 'none';
          return;
        }

        this.variantToggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const open = this.variantPopover.classList.toggle('is-open');
          this.variantToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });

        this.outsideClickHandler = (e) => {
          if (!this.variantPopover.contains(e.target) && !this.variantToggle.contains(e.target)) {
            this.variantPopover.classList.remove('is-open');
            this.variantToggle.setAttribute('aria-expanded', 'false');
          }
        };
        document.addEventListener('click', this.outsideClickHandler);

        this.attachOptionButtonHandlers();
      }

      attachOptionButtonHandlers() {
        this.variantPopover.querySelectorAll('[data-option-value]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            if (btn.hasAttribute('disabled')) return;
            this.selectVariantOption(btn.dataset.optionName, btn.dataset.optionValue, btn);
          });
        });
      }

      selectVariantOption(optionName, optionValue, clickedBtn) {
        const sibs = clickedBtn.parentElement.querySelectorAll('[data-option-value]');
        sibs.forEach((s) => s.removeAttribute('aria-pressed'));
        clickedBtn.setAttribute('aria-pressed', 'true');

        const clickedIdx = this.productOptions.indexOf(optionName);

        // Step 1: Update OTHER levels based on new selection at clicked level.
        // For each button at another level, check if an available variant
        // exists with the clicked value at clickedIdx and this value at otherIdx.
        this.productOptions.forEach((otherName, otherIdx) => {
          if (otherIdx === clickedIdx) return;

          const otherButtons = this.variantPopover?.querySelectorAll(
            `[data-option-name="${CSS.escape(otherName)}"][data-option-value]`
          );
          if (!otherButtons) return;

          otherButtons.forEach((btn) => {
            const hasMatch = this.variants.some((v) => {
              if (!v.available) return false;
              if (v.options[clickedIdx] !== optionValue) return false;
              return v.options[otherIdx] === btn.dataset.optionValue;
            });
            if (hasMatch) btn.removeAttribute('disabled');
            else btn.setAttribute('disabled', 'disabled');
          });

          // Auto-select first available if current is disabled
          const selBtn = this.variantPopover?.querySelector(
            `[data-option-name="${CSS.escape(otherName)}"][aria-pressed="true"]`
          );
          if (selBtn && selBtn.hasAttribute('disabled')) {
            const firstAvail = this.variantPopover?.querySelector(
              `[data-option-name="${CSS.escape(otherName)}"][data-option-value]:not([disabled])`
            );
            if (firstAvail) {
              selBtn.removeAttribute('aria-pressed');
              firstAvail.setAttribute('aria-pressed', 'true');
            }
          }
        });

        // Step 2: Update CLICKED level — a value stays enabled if ANY
        // available variant exists for it (regardless of other levels'
        // current selection). This keeps all materials selectable as long
        // as they have at least one available variant.
        const clickedButtons = this.variantPopover?.querySelectorAll(
          `[data-option-name="${CSS.escape(optionName)}"][data-option-value]`
        );
        if (clickedButtons) {
          clickedButtons.forEach((btn) => {
            const hasMatch = this.variants.some((v) => {
              if (!v.available) return false;
              return v.options[clickedIdx] === btn.dataset.optionValue;
            });
            if (hasMatch) btn.removeAttribute('disabled');
            else btn.setAttribute('disabled', 'disabled');
          });
        }

        // Step 3: Apply the matching variant
        const variant = this.findVariantBySelections();
        if (variant) {
          this.applyVariant(variant);
        } else {
          if (this.submitButton) {
            this.submitButton.setAttribute('disabled', 'disabled');
            const t = this.submitButton.querySelector('[data-submit-text]');
            if (t) t.textContent = window.variantStrings?.unavailable || 'Non disponibile';
          }
        }
      }

      /* ================================================================== */

      setupSubmitButton() {
        if (!this.submitButton) return;

        this.submitButton.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.submitButton.hasAttribute('disabled')) return;

          this.submitButton.classList.add('is-loading');
          this.submitButton.setAttribute('aria-disabled', 'true');
          const sp = this.submitButton.querySelector('.loading__spinner');
          if (sp) sp.classList.remove('hidden');

          const fd = new FormData(this.hiddenForm);
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

          if (cart) {
            fd.append('sections', cart.getSectionsToRender().map((s) => s.id));
            fd.append('sections_url', window.location.pathname);
            cart.setActiveElement(document.activeElement);
          }

          const cfg = fetchConfig('javascript');
          cfg.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete cfg.headers['Content-Type'];
          cfg.body = fd;

          fetch((window.routes?.cart_add_url) || '/cart/add', cfg)
            .then((r) => r.json())
            .then((r) => {
              if (r.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'sticky-add-to-cart',
                  productVariantId: fd.get('id'),
                  errors: r.errors || r.description,
                  message: r.message,
                });
                return;
              }
              if (!cart) { window.location = (window.routes?.cart_url) || '/cart'; return; }
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'sticky-add-to-cart',
                productVariantId: fd.get('id'),
                cartData: r,
              });
              cart.renderContents(r);
            })
            .catch((err) => console.error('Sticky ATC error:', err))
            .finally(() => {
              this.submitButton.classList.remove('is-loading');
              this.submitButton.removeAttribute('aria-disabled');
              const sp = this.submitButton.querySelector('.loading__spinner');
              if (sp) sp.classList.add('hidden');
            });
        });
      }

      handleCartUpdate() {
        this.submitButton?.classList.remove('is-loading');
        this.submitButton?.removeAttribute('aria-disabled');
      }

      handleCartError() {
        this.submitButton?.classList.remove('is-loading');
        this.submitButton?.removeAttribute('aria-disabled');
      }
    }
  );
}
